"use client"

import { useSearchParams } from "next/navigation"

import { cn } from "@/lib/utils"
import {
  CardPartenaire,
  Pagination,
  TPagination,
  type TCardPartenaireProps,
} from "@/components/ui/molecules"

type TCardPartenaireListProps = {
  className?: string
  list: TCardPartenaireProps[]
  pageUrl?: string
  totalPageCount?: number
  preview?: boolean
  codename: string
}

const CardPartenaireList = ({
  list = [],
  className,
  pageUrl,
  totalPageCount,
  codename,
  preview = false,
}: TCardPartenaireListProps) => {
  const searchParams = useSearchParams()
  const currentPageParam = searchParams.get("page")
  const parsedPage = currentPageParam ? parseInt(currentPageParam, 10) : 1
  const limitNews = 10

  // Total partners count is simply the list length (if provided)
  const total = list?.length ?? totalPageCount ?? 0
  const pageCount = Math.max(1, Math.ceil(total / limitNews))

  // Clamp current page into [1, pageCount]
  const currentPage = Math.min(Math.max(parsedPage || 1, 1), pageCount)

  // Slice the partners for the current page
  const offset = (currentPage - 1) * limitNews
  const partners = list ? list.slice(offset, offset + limitNews) : []

  const showPagination = total > limitNews

  // In order make pagination work with preview mode, we need to change the pageUrl to the codename
  if (preview) {
    pageUrl = codename
  }

  // Build pagination list - always include explicit ?page=1 for consistency
  const paginationList: TPagination[] = []
  for (let i = 1; i <= pageCount; i++) {
    const url = i === 1 ? `${pageUrl}` : `${pageUrl}?page=${i}`
    paginationList.push({
      label: i,
      isCurrent: i === currentPage,
      url,
    })
  }

  return (
    <div className={cn("bg-neutral-200 py-10 lg:py-17", className)}>
      <div className="container flex-col">
        {partners.map((cta) => {
          return (
            <CardPartenaire
              className="mb-6"
              key={cta.title}
              title={cta.title}
              body={cta.body}
              src={cta.src}
              alt={cta.alt}
              link={cta.link}
              linkText={cta.linkText}
            />
          )
        })}
      </div>
      {showPagination ? (
        <footer className="flex items-center justify-center pt-12">
          <Pagination list={paginationList} />
        </footer>
      ) : null}
    </div>
  )
}

export { CardPartenaireList, type TCardPartenaireListProps }
